<template>
  <div>
    <page-header :title="$t('iamee.organization')" />
    <page-body v-if="loaded">
      <a-button v-if="!organization.id" type="primary" @click="createOrganization">{{$t('iamee.create_organization')}}</a-button>
      <div class="d-flex org-layout" v-else>
        <organization
          ref="organization"
          :organization="organization"
          :syncLoading="syncLoading"
          @updateNodeList="updateNodeList"
          @updateCurrentNode="updateCurrentNode"
          @refresh="refreshOrganization"
          @sync="syncOrganization" />
        <div class="resource-container">
          <div class="org-info mb-3">
            <a-row :gutter="8">
              <a-col :span="12">
                <div class="info-item d-flex">
                  <div class="info-label">{{$t('table.title.name')}}：</div>
                  <div class="info-value">{{currentNode.name}}</div>
                </div>
              </a-col>
              <a-col :span="12">
                <div class="info-item d-flex">
                  <div class="info-label">{{$t('iamee.parent_organization')}}：</div>
                  <div class="info-value">{{currentNode.parentName}}</div>
                </div>
              </a-col>
            </a-row>
            <a-row :gutter="8">
              <a-col :span="12">
                <div class="info-item d-flex align-items-center">
                  <div class="info-label">{{$t('iamee.weight')}}：</div>
                  <div class="info-value d-flex align-items-center">
                    <span>{{currentNode.weight}}</span>
                    <a-icon
                      v-if="canEdit && currentNode.id && currentNode.id !== 'root'"
                      type="edit"
                      class="primary-color info-edit-icon"
                      @click="handleEditNode" />
                  </div>
                </div>
              </a-col>
              <a-col :span="12">
                <div class="info-item d-flex align-items-center">
                  <div class="info-label">{{$t('table.title.enable_status')}}：</div>
                  <div class="info-value d-flex align-items-center">
                    <status :status="organization.enabled" status-module="enabled" />
                    <a-icon
                      v-if="canEdit && organization.id"
                      type="edit"
                      class="primary-color info-edit-icon"
                      @click="handleToggleOrganizationEnabled" />
                  </div>
                </div>
              </a-col>
            </a-row>
            <a-row>
              <a-col>
                <div class="info-item d-flex align-items-center">
                  <div class="info-label">{{$t('table.title.desc')}}：</div>
                  <div class="info-value d-flex align-items-center">
                    <span>{{currentNode.description}}</span>
                    <a-icon
                      v-if="canEdit && currentNode.id"
                      type="edit"
                      class="primary-color info-edit-icon"
                      @click="handleEditNode" />
                  </div>
                </div>
              </a-col>
            </a-row>
          </div>
          <component :is="orgType" :id="listId" :organization="organization" :currentNode="currentNode" />
        </div>
      </div>
    </page-body>
  </div>
</template>

<script>
import WindowsMixin from '@/mixins/windows'
import { hasPermission } from '@/utils/auth'
import Project from './components/Project'
import Domain from './components/Domain'
import Organization from './components/Organization'

export default {
  name: 'OrganizationIndex',
  components: {
    Project,
    Domain,
    Organization,
  },
  mixins: [WindowsMixin],
  data () {
    return {
      loaded: false,
      syncLoading: false,
      timer: null,
      listId: 'GroupList',
      organization: {},
      nodeList: [],
      currentNodeKey: 'root',
    }
  },
  computed: {
    orgType () {
      return this.organization.type || 'create'
    },
    canEdit () {
      return hasPermission({ key: 'organizations_update' })
    },
    currentNode () {
      const targets = this.nodeList.filter(item => item.key === this.currentNodeKey)
      if (targets[0]) {
        const { tagValue, description, weight, tagKey = '-', key, tags = [] } = targets[0]
        return {
          id: key,
          name: tagValue,
          parentName: this.formatParentPath(targets[0]),
          description: description || '-',
          weight: key === 'root' ? '-' : weight || 1,
          levelName: tagKey.replace('org:', ''),
          tags,
        }
      }
      return {}
    },
  },
  created () {
    this.$oM = new this.$Manager('organizations')
    this.initOrganization()
  },
  beforeDestroy () {
    clearTimeout(this.timer)
    this.timer = null
  },
  methods: {
    // 父组织展示为从首个节点开始的完整路径，如：河北 / 石家庄
    formatParentPath (node) {
      if (!node || node.key === 'root') return '-'
      const full = node.parentFullLabel || node.parentLabel
      if (!full) return '-'
      // 根组织名不作为路径首段时，仅展示节点链；parentFullLabel 为节点链用 ^^ 拼接
      const parts = String(full).split('^^').filter(Boolean)
      if (!parts.length) return '-'
      // 若只有组织根名称且无下级节点路径，直接展示
      return parts.join(' / ')
    },
    async initOrganization () {
      try {
        const res = await this.$oM.list({
          params: { scope: this.$store.getters.scope },
        })
        const { data = [] } = res.data
        if (data.length) {
          this.organization = data[0]
        } else {
          this.organization = {}
        }
        this.loaded = true
      } catch (err) { throw err }
    },
    updateNodeList (list) {
      this.nodeList = list
    },
    updateCurrentNode (key) {
      this.currentNodeKey = key
    },
    refreshOrganization () {
      this.initOrganization()
    },
    createOrganization () {
      this.createDialog('OrganizationCreateDialog', {
        success: (org) => {
          this.initOrganization()
        },
      })
    },
    handleEditNode () {
      if (!this.currentNodeKey) return
      this.createDialog('OrganizationNodeEditDialog', {
        nodeList: this.nodeList,
        nodeId: this.currentNodeKey,
        orgId: this.organization.id,
        success: (type) => {
          if (type === 'organizations') {
            this.refreshOrganization()
          }
          this.$refs.organization && this.$refs.organization.initOrgList()
        },
      })
    },
    handleToggleOrganizationEnabled () {
      if (!this.organization || !this.organization.id) return
      const action = this.organization.enabled ? 'disable' : 'enable'
      this.createDialog('EnabledSwitchDialog', {
        vm: this,
        data: [this.organization],
        columns: [
          {
            field: 'name',
            title: this.$t('table.title.name'),
          },
          {
            field: 'description',
            title: this.$t('table.title.desc'),
          },
        ],
        action,
        fields: ['name', 'description', 'status'],
        resourceName: this.$t('iamee.organization'),
        onOk: async () => {
          await new this.$Manager('organizations').performAction({
            action,
            id: this.organization.id,
          })
          this.refreshOrganization()
        },
      })
    },
    async syncOrganization () {
      if (this.syncLoading) return
      try {
        this.syncLoading = true
        const { data } = await this.$oM.performAction({
          id: this.organization.id,
          action: 'sync',
        })
        if (data.status === 'sync') {
          this.startSyncTimer()
          this.hideSyncLoadingFun = this.$message.loading(this.$t('iamee.syncing'), 0)
        } else {
          this.organization = data
          this.syncLoading = false
          this.$refs.organization.initOrgList()
        }
      } catch (err) {
        this.syncLoading = false
        throw err
      }
    },
    startSyncTimer () {
      try {
        if (this.timer) {
          clearTimeout(this.timer)
          this.timer = null
        }
        this.timer = setTimeout(async () => {
          const { data } = await this.$oM.get({
            id: this.organization.id,
          })
          if (data.status === 'sync') {
            this.startSyncTimer()
          } else {
            clearTimeout(this.timer)
            this.timer = null
            this.syncLoading = false
            this.organization = data
            this.hideSyncLoadingFun && this.hideSyncLoadingFun()
            this.$refs.organization.initOrgList()
          }
        }, 10 * 1000)
      } catch (err) {
        this.syncLoading = false
        this.hideSyncLoadingFun && this.hideSyncLoadingFun()
        throw err
      }
    },
  },
}
</script>

<style lang="less" scoped>
.org-layout {
  align-items: flex-start;
  width: 100%;
  min-width: 0;
}
.resource-container {
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
}
.org-info {
  background: #f1f1f1;
  padding: 5px;
}
.info-item {
  padding: 10px 10px;
}
.info-label {
  margin-right: 5px;
  flex: 0 0 80px;
}
.info-value {
  word-wrap: break-word;
}
.info-edit-icon {
  margin-left: 8px;
  cursor: pointer;
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.2s;
}
.info-value:hover .info-edit-icon {
  opacity: 1;
}
</style>
